import React, { PropsWithChildren } from "react";

import { css, cx } from "@emotion/css";
import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, useForm } from "react-hook-form";

import { useStyles } from "@/hooks/useStyles.ts";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import {
  NurseInclusionPatientCreationInfo,
  nurseInclusionPatientCreationInfoSchema,
} from "@/uiKit/organisms/PatientCreationAndRenewal/NurseInclusion/NurseInclusionPatientCreationInfo.ts";
import { usePatientCreationContext } from "@/uiKit/organisms/PatientCreationAndRenewal/PatientCreationProvider.tsx";

export const NURSE_INCLUSION_PATIENT_CREATION_FORM_ID = "NURSE_INCLUSION_PATIENT_CREATION_FORM";

export type NurseInclusionPatientCreationFormProps = PropsWithChildren<{
  defaultValues: Partial<NurseInclusionPatientCreationInfo>;
  onSubmit: (data: NurseInclusionPatientCreationInfo) => void;
  className?: string;
}>;

export const NurseInclusionPatientCreationForm: React.FC<NurseInclusionPatientCreationFormProps> = ({
  defaultValues,
  onSubmit,
  className,
  children,
}) => {
  const styles = useStyles(makeStyles);
  const { carePlanCreationDataDirectives, telemonitoringNurses } = usePatientCreationContext();
  const providers = Queries.practitioner.useHSPList({});
  const defaultProvider = providers.length === 0 ? "other" : providers.length === 1 ? providers[0].hspId : undefined;
  const defaultNurse = telemonitoringNurses.length >= 1 ? telemonitoringNurses[0].id : undefined;
  const form = useForm<NurseInclusionPatientCreationInfo>({
    resolver: zodResolver(nurseInclusionPatientCreationInfoSchema) as Resolver<NurseInclusionPatientCreationInfo>,
    defaultValues: {
      ...defaultValues,
      periodStart: carePlanCreationDataDirectives.periodStart,
      duration: "3",
      homeServiceProviderId: defaultProvider,
      telemonitoringNurseId: defaultNurse,
    },
  });

  return (
    <Form
      id={NURSE_INCLUSION_PATIENT_CREATION_FORM_ID}
      formContext={form}
      onSubmit={onSubmit}
      className={cx(styles.root, className)}
    >
      {children}
    </Form>
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(8),
    overflow: "clip",
  }),
});
